import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ConversationCheckAvailability } from '@tomis-tech/types';

import {
  Overlay,
  ErrorMessage,
  Button,
  ButtonsContainer,
  ScrollContainer,
  TitleText,
} from './styled-components';
import { SavingSpinner } from './common';
import { useCheckAvailability, useSession, useParent } from '../context';
import { themeBlue } from '../consts';
import { createCheckAvailabilityUserActionMessage } from '../messages';
import { updateConversationCheckAvailability } from '../services/checkAvailability';

// Styled Components
const NumberInput = styled.input`
  padding: 0;
  margin: 0;
  align-self: center;
  width: 60px;
  height: 40px;
  border-radius: 5px;
  text-align: center;
  font-size: 28px;
  border: none;
`;

const OtherContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 50px;
  justify-content: center;
  margin-top: 10px;
`;

const CheckAvailabilityQuantity: React.FC = () => {
  const {
    changingQuantity,
    setChangingQuantity,
    setHeaderErrorMessage,
  } = useCheckAvailability();
  const { conversation } = useSession();
  const { pageView } = useParent();

  // guaranteed to exist on mount
  const { checkAvailability } = conversation.value;

  const [quantity, setQuantity] = useState(
    checkAvailability.quantity.toString()
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  async function saveNewQuantity(quantity: number) {
    setIsSaving(true);
    setErrorMessage('');
    setHeaderErrorMessage('');

    if (quantity <= 0) {
      setIsSaving(false);
      setErrorMessage('Please select a number greater than zero');
      return;
    }

    const updatedCheckAvailability: ConversationCheckAvailability = {
      ...checkAvailability,
      quantity,
    };

    const message = createCheckAvailabilityUserActionMessage(
      `Number of participants: ${quantity}`,
      checkAvailability,
      pageView
    );

    const { error } = await updateConversationCheckAvailability({
      conversation,
      message,
      updatedCheckAvailability,
    });

    setIsSaving(false);
    setChangingQuantity(false);

    if (error) {
      setErrorMessage('Error saving quantity');
    }
  }

  if (!changingQuantity) return <></>;

  return (
    <Overlay>
      <ScrollContainer>
        <TitleText>
          How many people are going? Adults and children combined.
        </TitleText>
        <OtherContainer>
          <NumberInput
            inputMode="numeric"
            value={quantity}
            type="number"
            onChange={e => {
              setQuantity(e.target.value);
            }}
            min={1}
            max={99}
          />
        </OtherContainer>
      </ScrollContainer>
      <ButtonsContainer>
        <Button
          color={themeBlue}
          disabled={isSaving}
          onClick={() => saveNewQuantity(Number(quantity))}
        >
          {isSaving ? <SavingSpinner /> : 'SAVE'}
        </Button>
        <Button
          color="gray"
          disabled={isSaving}
          onClick={() => setChangingQuantity(false)}
          style={{ marginTop: '10px' }}
        >
          CANCEL
        </Button>
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </ButtonsContainer>
    </Overlay>
  );
};

export default CheckAvailabilityQuantity;
